/* ==========================================================================
   Young Grandpa - PDP trust badge modal, mobile only
   --------------------------------------------------------------------------
   Below 1024px, tapping a trust badge (blocks/yg-trust-item.liquid) opens a
   centred modal card over a dimmed overlay instead of the desktop hover/
   focus tooltip bubble - assets/yg-custom.css hides the bubble and turns
   .yg-trust__modal into the actual modal layout only in that same range.

   State is a plain .is-open class, not :focus/:focus-within. The previous
   version of this mobile presentation gated the close button's very
   pointer-events on :focus-within timing, which turned out not to hold up
   reliably against real touch input - the tap would land while the button
   wasn't actually interactive yet, or focus never made it where the CSS
   expected. A modal needs explicit, JS-owned open/close state anyway: the
   overlay and Escape both have to close it, which plain :focus can't
   express regardless of the timing issue.

   No scroll lock. Nothing here sets or restores document/body overflow -
   the page can still scroll behind the overlay while it's open, by design.

   No focus trap (Tab cycling within the card). Close returns focus to the
   trigger, which covers part of the keyboard path (Escape closes it); flag
   if full Tab trapping is wanted too.

   Open does NOT call .focus() on the close button. It did in an earlier
   version, synchronously, in the same tick the modal starts its opacity/
   scale transition in - calling .focus() while an element is also mid
   transition can make a mobile browser try to scroll/adjust the viewport to
   keep the newly-focused element in view, right as the user's opening tap
   is still resolving into a synthesized click. The overlay has no .focus()
   call anywhere near it and closes reliably; the close button, which did,
   didn't. Removed rather than deferred with a timeout, since nothing here
   actually requires the close button to hold focus on open.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE = '(max-width: 1023px)';
  var ITEM = '.product-information .yg-trust__item';

  var mobile = window.matchMedia(MOBILE);
  var bound = new WeakSet();
  var observer = new MutationObserver(schedule);
  var scheduled = false;
  var openModal = null;
  var openTrigger = null;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      init();
    });
  }

  function init() {
    var items = document.querySelectorAll(ITEM);

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      /* Sections re-render in the theme editor, replacing the item outright.
         Watching the parent lets us bind whatever replaces it. */
      if (item.parentElement) observer.observe(item.parentElement, { childList: true });

      if (bound.has(item)) continue;

      var trigger = item.querySelector('.yg-trust__trigger');
      var modal = item.querySelector('.yg-trust__modal');
      if (!trigger || !modal) continue;

      bound.add(item);
      trigger.addEventListener('click', handleTriggerClick(trigger, modal));
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @param {HTMLElement} modal
   */
  function handleTriggerClick(trigger, modal) {
    return function () {
      if (!mobile.matches) return;
      open(modal, trigger);
    };
  }

  /**
   * @param {HTMLElement} modal
   * @param {HTMLElement} trigger
   */
  function open(modal, trigger) {
    if (openModal === modal) return;
    if (openModal) close();

    modal.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    openModal = modal;
    openTrigger = trigger;

    document.addEventListener('keydown', handleKeydown);
  }

  function close() {
    if (!openModal) return;

    openModal.classList.remove('is-open');
    document.removeEventListener('keydown', handleKeydown);

    if (openTrigger) {
      openTrigger.setAttribute('aria-expanded', 'false');
      openTrigger.focus();
    }

    openModal = null;
    openTrigger = null;
  }

  /**
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    if (event.key === 'Escape') close();
  }

  document.addEventListener('click', function (event) {
    /* Element, not HTMLElement: the close button's icon is an inline <svg>
       with <path> children (blocks/yg-trust-item.liquid), and SVGElement is
       a sibling of HTMLElement under Element, not a subtype of it. A tap
       landing on the icon itself - the visible × - made event.target an
       SVGElement/SVGPathElement, which failed an instanceof HTMLElement
       guard here and returned before .closest() ever ran. The overlay is a
       plain childless <div>, always HTMLElement, which is why it never hit
       this. .closest() itself is defined on Element.prototype and works
       identically for both. */
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-yg-trust-dismiss]')) close();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
